import React, { useMemo, useState, useEffect, useRef } from "react";
import {
  Box, Typography, MenuItem, TextField, Select, FormControl, InputLabel, Button, Chip, Menu, Tooltip,
  Dialog, DialogActions, DialogContent, DialogTitle, CircularProgress, IconButton, InputAdornment,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TableSortLabel,
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import SaveIcon from "@mui/icons-material/BookmarkAdd";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ScheduleSendIcon from "@mui/icons-material/ScheduleSend";
import SearchIcon from "@mui/icons-material/Search";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import {
  ResponsiveContainer, BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip as RTooltip,
} from "recharts";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import { C, bandColor, bandLabel, fmtUSD, fmtFull, exportCSV, exportExcel, exportPDF } from "./reportingUtils";
import { nlReportQuery } from "../api/claudeApi";
import { createReportSchedule, sendTestReport, listReportTemplates, saveReportTemplate, deleteReportTemplate } from "../api/underwritingApi";
import { toast } from "react-toastify";
import { createEmailActionRunner, emailActionOutcome } from "../utils/emailAction";

const METRICS = [
  { key: "count", label: "Counterparties (count)", agg: (g) => g.length, money: false },
  { key: "recExposure", label: "Recommended exposure", agg: (g) => g.reduce((a, r) => a + r.recLimit, 0), money: true },
  { key: "appExposure", label: "Approved exposure", agg: (g) => g.reduce((a, r) => a + r.appLimit, 0), money: true },
  { key: "avgScore", label: "Average ARC score", agg: (g) => { const s = g.filter((r) => r.score != null); return s.length ? +(s.reduce((a, r) => a + r.score, 0) / s.length).toFixed(2) : 0; }, money: false },
];
const DIMENSIONS = [
  { key: "sector", label: "Sector", of: (r) => r.sector || "Unspecified" },
  { key: "region", label: "Region", of: (r) => r.region || "Other" },
  { key: "country", label: "Country", of: (r) => r.country || "Unknown" },
  { key: "status", label: "Approval status", of: (r) => (r.status || "PENDING").toString() },
  { key: "band", label: "Risk band", of: (r) => bandLabel(r.score) },
  { key: "none", label: "No grouping (total)", of: () => "All counterparties" },
];
const CHART_TYPES = ["Bar", "Pie", "Table"];
const CHART_PALETTES = {
  default: [C.ink, C.blue, C.green, C.amber, C.violet, C.red, C.muted, "#0EA5A5", "#D9488A"],
  blue: ["#173B75", "#2F6BE0", "#6EA0FF", "#99BAFF", "#C4D6FF"],
  green: ["#195C43", "#278A63", "#43AA7B", "#77C8A5", "#B6E1CE"],
  warm: ["#B83A3A", "#E24B4A", "#E98545", "#E7A13C", "#F2C66D"],
  purple: ["#4C3399", "#6A4AE0", "#7A5AF8", "#A18AFB", "#CABDFD"],
  multicolor: [C.blue, C.green, C.amber, C.violet, C.red, "#0EA5A5", "#D9488A"],
};
const PALETTES = Object.keys(CHART_PALETTES).map((value) => ({ value, label: value[0].toUpperCase() + value.slice(1) }));
const LS_KEY = "crediarc_bi_report_templates";

const loadTemplates = () => { try { return JSON.parse(localStorage.getItem(LS_KEY) || "[]"); } catch { return []; } };
const saveTemplates = (t) => { try { localStorage.setItem(LS_KEY, JSON.stringify(t)); } catch { /* ignore */ } };
const getTemplateSpec = (template) => template.spec || { metric: template.metricKey, dimension: template.dimKey, chartType: template.chartType, filters: { minScore: template.minScore, maxScore: template.maxScore, status: template.statusFilter } };
const getTemplateUpdated = (template) => template.updatedAt || template.createdAt || template.updated_at || template.created_at || null;
const templateFilters = (template) => {
  const filters = getTemplateSpec(template).filters || {};
  const parts = [];
  if (filters.status && filters.status !== "All") parts.push(filters.status);
  if (filters.minScore != null && filters.minScore !== "") parts.push(`ARC ≥ ${filters.minScore}`);
  if (filters.maxScore != null && filters.maxScore !== "") parts.push(`ARC ≤ ${filters.maxScore}`);
  return parts.length ? parts.join(" · ") : "All counterparties";
};

const ReportBuilder = ({ rows, onDrill, tenantId }) => {
  const [metricKey, setMetricKey] = useState("recExposure");
  const [dimKey, setDimKey] = useState("sector");
  const [chartType, setChartType] = useState("Bar");
  const [minScore, setMinScore] = useState("");
  const [maxScore, setMaxScore] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const [palette, setPalette] = useState("default");
  const [templates, setTemplates] = useState(loadTemplates());
  const [exportEl, setExportEl] = useState(null);
  const [nlText, setNlText] = useState("");
  const [nlLoading, setNlLoading] = useState(false);
  const [templateDialogOpen, setTemplateDialogOpen] = useState(false);
  const [templateName, setTemplateName] = useState("");
  const [conversation, setConversation] = useState([]);
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [scheduleName, setScheduleName] = useState("");
  const [scheduleCadence, setScheduleCadence] = useState("weekly");
  const [scheduleRecipients, setScheduleRecipients] = useState("");
  const [saving, setSaving] = useState(false);
  const [sendingTest, setSendingTest] = useState(false);
  const [savedSearch, setSavedSearch] = useState("");
  const [savedSort, setSavedSort] = useState("updated");
  const [savedSortDirection, setSavedSortDirection] = useState("desc");
  const [reportMenuEl, setReportMenuEl] = useState(null);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [renameOpen, setRenameOpen] = useState(false);
  const [renameName, setRenameName] = useState("");
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [scheduleSpec, setScheduleSpec] = useState(null);
  const chartRef = useRef(null);
  const chatRef = useRef(null);
  const runEmailAction = useRef(createEmailActionRunner());

  const applySpec = (spec) => {
    if (!spec) return;
    if (spec.metric) setMetricKey(spec.metric);
    if (spec.dimension) setDimKey(spec.dimension);
    if (spec.chartType) setChartType(spec.chartType);
    setMinScore(spec.filters?.minScore != null ? String(spec.filters.minScore) : "");
    setMaxScore(spec.filters?.maxScore != null ? String(spec.filters.maxScore) : "");
    setStatusFilter(spec.filters?.status || "All");
    if (spec.palette) setPalette(spec.palette);
  };
  const currentSpec = useMemo(() => ({
    metric: metricKey, dimension: dimKey, chartType, palette,
    filters: { minScore: minScore === "" ? null : Number(minScore), maxScore: maxScore === "" ? null : Number(maxScore), status: statusFilter },
    title: `${METRICS.find((m) => m.key === metricKey)?.label} by ${DIMENSIONS.find((d) => d.key === dimKey)?.label}`,
  }), [metricKey, dimKey, chartType, palette, minScore, maxScore, statusFilter]);
  const runNL = async () => {
    const q = nlText.trim();
    if (!q || nlLoading) return;
    setNlLoading(true);
    try {
      const nextMessages = [...conversation, { role: "user", content: q }];
      setConversation(nextMessages);
      setNlText("");
      const spec = await nlReportQuery(q, { currentSpec, messages: nextMessages });
      if (spec) {
        applySpec(spec);
        setConversation((messages) => [...messages, { role: "assistant", content: spec.reply || `Updated the report to ${spec.title || "your requested view"}.` }]);
      }
      else toast.info("Couldn't interpret that — try rephrasing.");
    } catch (e) {
      toast.error(e?.response?.data?.message || "Couldn't run that query.");
    } finally {
      setNlLoading(false);
    }
  };

  const metric = METRICS.find((m) => m.key === metricKey);
  const dim = DIMENSIONS.find((d) => d.key === dimKey);

  const filtered = useMemo(() => {
    return (rows || []).filter((r) => {
      if (minScore !== "" && (r.score == null || r.score < parseFloat(minScore))) return false;
      if (maxScore !== "" && (r.score == null || r.score > parseFloat(maxScore))) return false;
      if (statusFilter !== "All" && (r.status || "PENDING").toUpperCase() !== statusFilter.toUpperCase()) return false;
      return true;
    });
  }, [rows, minScore, maxScore, statusFilter]);

  const data = useMemo(() => {
    const groups = {};
    filtered.forEach((r) => { const k = dim.of(r); (groups[k] = groups[k] || []).push(r); });
    return Object.entries(groups)
      .map(([name, g]) => ({ name, value: metric.agg(g), count: g.length, _rows: g }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 14);
  }, [filtered, dim, metric]);

  const statuses = useMemo(() => ["All", ...Array.from(new Set((rows || []).map((r) => (r.status || "PENDING"))))], [rows]);

  const exportHeaders = [{ key: "name", label: dim.label }, { key: "value", label: metric.label }, { key: "count", label: "Counterparties" }];
  const exportData = data.map((d) => ({ name: d.name, value: metric.money ? Math.round(d.value) : d.value, count: d.count }));
  const fname = `CrediArc-Report-${metric.key}-by-${dim.key}`;
  const doExport = (fmt) => {
    setExportEl(null);
    const title = `${metric.label} by ${dim.label}`;
    try {
      if (fmt === "csv") exportCSV(exportData, exportHeaders, fname);
      else if (fmt === "xlsx") exportExcel(exportData, exportHeaders, fname, "Report");
      else {
        const svg = chartRef.current?.querySelector("svg");
        const chartSvg = svg ? new XMLSerializer().serializeToString(svg.cloneNode(true)) : "";
        if (!exportPDF(exportData, exportHeaders, fname, title, `Filtered to ${filtered.length} counterparties`, { chartSvg })) {
        toast.error("Allow pop-ups to export this report as PDF.");
        }
      }
    } catch (error) {
      toast.error("Couldn't export this report. Please try again.");
    }
  };

  const doSave = () => {
    setTemplateName(`${metric.label} by ${dim.label}`);
    setTemplateDialogOpen(true);
  };
  const confirmSave = async () => {
    const name = templateName.trim();
    if (!name) return;
    setSaving(true);
    try {
      const tpl = tenantId ? await saveReportTemplate(tenantId, { name, spec: currentSpec }) : { id: `${Date.now()}`, name, spec: currentSpec };
      const next = [...templates.filter((t) => t.name !== name), tpl];
      setTemplates(next); saveTemplates(next);
      setTemplateDialogOpen(false);
      toast.success("Report saved for this tenant.");
    } catch (e) { toast.error(e?.response?.data?.message || "Couldn't save this report."); }
    finally { setSaving(false); }
  };
  const applyTemplate = (t) => applySpec(t.spec || { metric: t.metricKey, dimension: t.dimKey, chartType: t.chartType, filters: { minScore: t.minScore, maxScore: t.maxScore, status: t.statusFilter } });
  const openTemplate = (template) => {
    applyTemplate(template);
    setReportMenuEl(null);
    setSelectedTemplate(null);
    requestAnimationFrame(() => chartRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }));
  };
  const delTemplate = async (id) => {
    try {
      if (tenantId) await deleteReportTemplate(tenantId, id);
      const next = templates.filter((t) => t.id !== id);
      setTemplates(next); saveTemplates(next);
      setDeleteTarget(null);
      toast.success("Saved report deleted.");
    }
    catch { toast.error("Couldn't delete this saved report."); }
  };
  const duplicateTemplate = async (template) => {
    const name = `${template.name} copy`;
    setSaving(true);
    try {
      const copy = tenantId ? await saveReportTemplate(tenantId, { name, spec: template.spec }) : { ...template, id: `${Date.now()}`, name };
      const next = [...templates, copy];
      setTemplates(next); saveTemplates(next);
      toast.success("Saved report duplicated.");
    } catch (e) { toast.error(e?.response?.data?.message || "Couldn't duplicate this report."); }
    finally { setSaving(false); setReportMenuEl(null); setSelectedTemplate(null); }
  };
  const confirmRename = async () => {
    const name = renameName.trim();
    if (!name || !selectedTemplate) return;
    setSaving(true);
    try {
      const replacement = tenantId ? await saveReportTemplate(tenantId, { name, spec: selectedTemplate.spec }) : { ...selectedTemplate, id: `${Date.now()}`, name };
      if (tenantId) await deleteReportTemplate(tenantId, selectedTemplate.id);
      const next = templates.map((template) => template.id === selectedTemplate.id ? replacement : template);
      setTemplates(next); saveTemplates(next);
      setRenameOpen(false); setSelectedTemplate(null);
      toast.success("Saved report renamed.");
    } catch (e) { toast.error(e?.response?.data?.message || "Couldn't rename this report."); }
    finally { setSaving(false); }
  };
  useEffect(() => {
    if (!tenantId) return;
    listReportTemplates(tenantId).then((items) => { setTemplates(items); saveTemplates(items); }).catch(() => toast.error("Couldn't load saved reports."));
  }, [tenantId]);
  useEffect(() => {
    if (chatRef.current) chatRef.current.scrollTop = chatRef.current.scrollHeight;
  }, [conversation]);

  const openSchedule = (name, spec) => {
    setScheduleName(name || spec?.title || currentSpec.title);
    setScheduleSpec(spec || currentSpec);
    setScheduleOpen(true);
  };

  const createSchedule = async () => {
    const recipients = scheduleRecipients.split(/[,;\s]+/).map((v) => v.trim()).filter(Boolean);
    if (!recipients.length) { toast.info("Add at least one recipient email."); return; }
    setSaving(true);
    try {
      await createReportSchedule(tenantId, { name: scheduleName.trim() || currentSpec.title, cadence: scheduleCadence, recipients, reportSpec: scheduleSpec || currentSpec });
      setScheduleOpen(false); setScheduleRecipients("");
      toast.success("Report schedule created.");
    } catch (e) { toast.error(e?.response?.data?.message || "Couldn't schedule this report."); }
    finally { setSaving(false); }
  };

  const sendTest = async () => {
    const recipients = scheduleRecipients.split(/[,;\s]+/).map((value) => value.trim()).filter(Boolean);
    if (!recipients.length) { toast.info("Add at least one recipient email."); return; }
    setSendingTest(true);
    try {
      const result = await runEmailAction.current({
        tenantId, action: "test-report",
        payload: { name: scheduleName.trim() || currentSpec.title, recipients, reportSpec: scheduleSpec || currentSpec },
        request: (payload) => sendTestReport(tenantId, payload),
      });
      const outcome = emailActionOutcome(result, "Test report");
      toast[outcome.status](outcome.message);
    } catch (e) { toast.error(e?.response?.data?.message || e?.message || "Couldn't send the test report."); }
    finally { setSendingTest(false); }
  };

  const AxisTick = ({ x, y, payload }) => {
    const full = String(payload?.value || "");
    const label = full.length > 36 ? `${full.slice(0, 35)}…` : full;
    return <g transform={`translate(${x},${y})`}><title>{full}</title><text x={-10} y={0} dy={4} textAnchor="end" fill={C.muted} fontSize={11}>{label}</text></g>;
  };

  const sel = (label, value, onChange, options) => (
    <FormControl size="small" sx={{ minWidth: 170 }}>
      <InputLabel sx={{ fontSize: 13 }}>{label}</InputLabel>
      <Select label={label} value={value} onChange={(e) => onChange(e.target.value)} sx={{ fontSize: 13.5, background: "#fff" }}>
        {options.map((o) => <MenuItem key={o.value} value={o.value} sx={{ fontSize: 13.5 }}>{o.label}</MenuItem>)}
      </Select>
    </FormControl>
  );

  const formatTemplateDate = (template) => {
    const value = getTemplateUpdated(template);
    if (!value) return "Not recorded";
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? "Not recorded" : date.toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
  };
  const visibleTemplates = useMemo(() => {
    const query = savedSearch.trim().toLowerCase();
    const result = templates.filter((template) => {
      const spec = getTemplateSpec(template);
      const metricLabel = METRICS.find((item) => item.key === spec.metric)?.label || spec.metric || "";
      const dimensionLabel = DIMENSIONS.find((item) => item.key === spec.dimension)?.label || spec.dimension || "";
      return !query || `${template.name} ${metricLabel} ${dimensionLabel} ${spec.chartType || ""} ${templateFilters(template)}`.toLowerCase().includes(query);
    });
    return result.sort((a, b) => {
      const direction = savedSortDirection === "asc" ? 1 : -1;
      if (savedSort === "name") return a.name.localeCompare(b.name) * direction;
      const aTime = new Date(getTemplateUpdated(a) || 0).getTime();
      const bTime = new Date(getTemplateUpdated(b) || 0).getTime();
      return (aTime - bTime) * direction;
    });
  }, [templates, savedSearch, savedSort, savedSortDirection]);
  const changeSavedSort = (column) => {
    if (savedSort === column) setSavedSortDirection((direction) => direction === "asc" ? "desc" : "asc");
    else { setSavedSort(column); setSavedSortDirection(column === "name" ? "asc" : "desc"); }
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: "14px" }}>
      {/* controls */}
      <Box sx={{ background: "#fff", border: `1px solid ${C.line}`, borderRadius: "12px", p: "16px 18px" }}>
        {conversation.length > 0 && (
          <Box ref={chatRef} aria-label="ArcAI conversation" sx={{
            display: "flex", flexDirection: "column", gap: 0.75, mb: 1.25, height: 190, overflowY: "auto", pr: 0.75,
            scrollbarWidth: "thin", scrollbarColor: `${C.muted} transparent`,
            "&::-webkit-scrollbar": { width: 6 }, "&::-webkit-scrollbar-thumb": { background: C.muted, borderRadius: 6 },
          }}>
            {conversation.map((message, index) => (
              <Box key={index} sx={{ alignSelf: message.role === "user" ? "flex-end" : "flex-start", maxWidth: "78%", px: 1.25, py: 0.75, borderRadius: "10px", background: message.role === "user" ? "#EEF4FF" : "#F4F0FF", color: C.slate, fontSize: 12.5 }}>
                <strong>{message.role === "user" ? "You" : "ArcAI"}:</strong> {message.content}
              </Box>
            ))}
          </Box>
        )}
        <Box sx={{ display: "flex", gap: 1, alignItems: "center", mb: 1.5, pb: 1.5, borderBottom: `1px solid ${C.line}` }}>
          <AutoAwesomeIcon sx={{ color: C.violet, flex: "0 0 auto" }} />
          <TextField value={nlText} onChange={(e) => setNlText(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") runNL(); }}
            placeholder={'Ask in plain English — e.g. "recommended exposure by region for high-risk companies"'} size="small" fullWidth
            sx={{ "& .MuiOutlinedInput-root": { fontSize: 13.5 } }} />
          <Button onClick={runNL} disabled={nlLoading} variant="contained"
            sx={{ textTransform: "none", background: C.violet, whiteSpace: "nowrap", "&:hover": { background: "#6a4ae0" } }}>
            {nlLoading ? "Refining…" : conversation.length ? "Refine" : "Ask ArcAI"}
          </Button>
        </Box>
        <Typography sx={{ fontSize: 15, fontWeight: 700, color: C.ink, mb: 1.5 }}>Build a report</Typography>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.25, alignItems: "center" }}>
          {sel("Measure", metricKey, setMetricKey, METRICS.map((m) => ({ value: m.key, label: m.label })))}
          {sel("Group by", dimKey, setDimKey, DIMENSIONS.map((d) => ({ value: d.key, label: d.label })))}
          {sel("Chart", chartType, setChartType, CHART_TYPES.map((t) => ({ value: t, label: t })))}
          {sel("Colors", palette, setPalette, PALETTES)}
          {sel("Status", statusFilter, setStatusFilter, statuses.map((s) => ({ value: s, label: s })))}
          <TextField label="Min ARC" size="small" value={minScore} onChange={(e) => setMinScore(e.target.value)} sx={{ width: 92 }} inputProps={{ inputMode: "decimal" }} />
          <TextField label="Max ARC" size="small" value={maxScore} onChange={(e) => setMaxScore(e.target.value)} sx={{ width: 92 }} inputProps={{ inputMode: "decimal" }} />
          <Box sx={{ flex: 1 }} />
          <Tooltip title="Save this report for everyone in this tenant"><Button startIcon={<SaveIcon />} onClick={doSave} sx={{ textTransform: "none", color: C.ink }}>Save report</Button></Tooltip>
          <Button startIcon={<ScheduleSendIcon />} onClick={() => openSchedule(currentSpec.title, currentSpec)} sx={{ textTransform: "none", color: C.ink }}>Schedule</Button>
          <Button startIcon={<DownloadIcon />} variant="outlined" onClick={(e) => setExportEl(e.currentTarget)} sx={{ textTransform: "none", borderColor: C.line, color: C.ink }}>Export</Button>
          <Menu anchorEl={exportEl} open={!!exportEl} onClose={() => setExportEl(null)}>
            <MenuItem onClick={() => doExport("csv")}>CSV</MenuItem><MenuItem onClick={() => doExport("xlsx")}>Excel</MenuItem><MenuItem onClick={() => doExport("pdf")}>PDF</MenuItem>
          </Menu>
        </Box>
      </Box>

      {/* result */}
      <Box ref={chartRef} sx={{ background: "#fff", border: `1px solid ${C.line}`, borderRadius: "12px", p: "18px 20px" }}>
        <Typography sx={{ fontSize: 15, fontWeight: 700, color: C.ink }}>{metric.label} by {dim.label}</Typography>
        <Typography sx={{ fontSize: 12.5, color: C.muted, mb: 1.5 }}>{filtered.length} of {(rows || []).length} counterparties after filters · click a {chartType === "Table" ? "row" : "segment"} to drill in</Typography>

        {data.length === 0 && <Typography sx={{ fontSize: 13, color: C.muted, py: 4, textAlign: "center" }}>No data for these filters.</Typography>}

        {chartType === "Bar" && data.length > 0 && (
          <Box sx={{ overflowX: "auto" }}>
            <Box sx={{ minWidth: 720, height: Math.max(240, data.length * 34) }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart layout="vertical" data={data} margin={{ left: 12, right: 30 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#EEF1F7" horizontal={false} />
                  <XAxis type="number" tick={{ fontSize: 11, fill: C.muted }} tickFormatter={metric.money ? fmtUSD : undefined} />
                  <YAxis type="category" dataKey="name" tick={<AxisTick />} interval={0} width={255} />
                  <RTooltip formatter={(v) => [metric.money ? fmtFull(v) : v, metric.label]} />
                  <Bar dataKey="value" radius={[0, 4, 4, 0]} cursor="pointer" onClick={(d) => onDrill?.(`${d.name} · ${dim.label}`, d._rows)}>
                    {data.map((d, i) => <Cell key={i} fill={palette === "default" && metricKey === "avgScore" ? bandColor(d.value) : CHART_PALETTES[palette][i % CHART_PALETTES[palette].length]} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </Box>
          </Box>
        )}

        {chartType === "Pie" && data.length > 0 && (
          <>
            <ResponsiveContainer width="100%" height={280}>
              <PieChart>
                <Pie data={data} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={58} outerRadius={105} paddingAngle={2}
                  onClick={(d) => onDrill?.(`${d.name} · ${dim.label}`, d._rows)} cursor="pointer">
                  {data.map((d, i) => <Cell key={i} fill={CHART_PALETTES[palette][i % CHART_PALETTES[palette].length]} />)}
                </Pie>
                <RTooltip formatter={(v) => [metric.money ? fmtFull(v) : v, metric.label]} />
              </PieChart>
            </ResponsiveContainer>
            <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: "6px 14px", mt: 0.5 }}>
              {data.map((d, i) => <Box key={d.name} sx={{ display: "flex", alignItems: "flex-start", gap: 0.75, minWidth: 0 }}>
                <Box sx={{ width: 9, height: 9, borderRadius: "50%", mt: "4px", flex: "0 0 auto", background: CHART_PALETTES[palette][i % CHART_PALETTES[palette].length] }} />
                <Typography title={d.name} sx={{ fontSize: 11.5, color: C.slate, lineHeight: 1.35 }}>{d.name}</Typography>
              </Box>)}
            </Box>
          </>
        )}

        {chartType === "Table" && data.length > 0 && (
          <Box sx={{ border: `1px solid ${C.line}`, borderRadius: "8px", overflow: "hidden" }}>
            <Box sx={{ display: "grid", gridTemplateColumns: "2fr 1fr 1fr", background: C.ground, px: 1.25, py: 0.75 }}>
              {[dim.label, metric.label, "Counterparties"].map((h) => <Typography key={h} sx={{ fontSize: 10.5, fontWeight: 700, textTransform: "uppercase", letterSpacing: ".04em", color: C.muted }}>{h}</Typography>)}
            </Box>
            {data.map((d, i) => (
              <Box key={i} onClick={() => onDrill?.(`${d.name} · ${dim.label}`, d._rows)}
                sx={{ display: "grid", gridTemplateColumns: "2fr 1fr 1fr", px: 1.25, py: 0.9, borderTop: `1px solid ${C.line}`, cursor: "pointer", "&:hover": { background: "#F7F9FE" } }}>
                <Typography sx={{ fontSize: 13, color: C.ink, fontWeight: 500 }}>{d.name}</Typography>
                <Typography sx={{ fontSize: 13, color: C.slate, fontVariantNumeric: "tabular-nums" }}>{metric.money ? fmtFull(d.value) : d.value}</Typography>
                <Typography sx={{ fontSize: 13, color: C.slate }}>{d.count}</Typography>
              </Box>
            ))}
          </Box>
        )}
      </Box>

      {/* saved reports */}
      <Box sx={{ background: "#fff", border: `1px solid ${C.line}`, borderRadius: "12px", overflow: "hidden" }}>
        <Box sx={{ px: { xs: 2, md: 2.5 }, py: 2, display: "flex", gap: 2, alignItems: { xs: "stretch", md: "center" }, flexDirection: { xs: "column", md: "row" }, borderBottom: `1px solid ${C.line}` }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, minWidth: 0 }}>
            <Box sx={{ width: 38, height: 38, borderRadius: "10px", display: "grid", placeItems: "center", background: "#EEF4FF", color: C.blue, flex: "0 0 auto" }}>
              <DescriptionOutlinedIcon fontSize="small" />
            </Box>
            <Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography sx={{ fontSize: 15, fontWeight: 700, color: C.ink }}>Saved reports</Typography>
                <Chip label={templates.length} size="small" sx={{ height: 22, background: C.ground, color: C.slate, fontWeight: 700, fontSize: 11 }} />
              </Box>
              <Typography sx={{ fontSize: 12, color: C.muted }}>Tenant-wide report library for your team</Typography>
            </Box>
          </Box>
          <Box sx={{ flex: 1 }} />
          <TextField
            value={savedSearch}
            onChange={(event) => setSavedSearch(event.target.value)}
            placeholder="Search saved reports"
            size="small"
            aria-label="Search saved reports"
            sx={{ width: { xs: "100%", md: 270 }, "& .MuiOutlinedInput-root": { fontSize: 13, background: "#fff" } }}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 18, color: C.muted }} /></InputAdornment> }}
          />
          <Button startIcon={<SaveIcon />} variant="contained" onClick={doSave} sx={{ textTransform: "none", whiteSpace: "nowrap" }}>Save current report</Button>
        </Box>

        {templates.length === 0 ? (
          <Box sx={{ px: 2, py: 5, textAlign: "center" }}>
            <DescriptionOutlinedIcon sx={{ fontSize: 34, color: C.muted, mb: 1 }} />
            <Typography sx={{ fontSize: 14, fontWeight: 700, color: C.ink }}>No saved reports yet</Typography>
            <Typography sx={{ fontSize: 12.5, color: C.muted, mt: 0.5, mb: 2 }}>Save the current report to make it available to everyone in this tenant.</Typography>
            <Button variant="outlined" startIcon={<SaveIcon />} onClick={doSave} sx={{ textTransform: "none" }}>Save current report</Button>
          </Box>
        ) : visibleTemplates.length === 0 ? (
          <Box sx={{ px: 2, py: 4, textAlign: "center" }}>
            <Typography sx={{ fontSize: 13.5, fontWeight: 600, color: C.ink }}>No reports match “{savedSearch}”</Typography>
            <Button onClick={() => setSavedSearch("")} sx={{ mt: 0.5, textTransform: "none" }}>Clear search</Button>
          </Box>
        ) : (
          <TableContainer sx={{ overflowX: "auto" }}>
            <Table size="small" aria-label="Saved reports">
              <TableHead>
                <TableRow sx={{ background: "#F8FAFD" }}>
                  <TableCell sx={{ minWidth: 230 }}><TableSortLabel active={savedSort === "name"} direction={savedSort === "name" ? savedSortDirection : "asc"} onClick={() => changeSavedSort("name")}><strong>Report</strong></TableSortLabel></TableCell>
                  <TableCell sx={{ minWidth: 190 }}><strong>Configuration</strong></TableCell>
                  <TableCell sx={{ minWidth: 170 }}><strong>Filters</strong></TableCell>
                  <TableCell sx={{ minWidth: 120 }}><TableSortLabel active={savedSort === "updated"} direction={savedSort === "updated" ? savedSortDirection : "desc"} onClick={() => changeSavedSort("updated")}><strong>Updated</strong></TableSortLabel></TableCell>
                  <TableCell align="right" sx={{ minWidth: 210 }}><strong>Actions</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {visibleTemplates.map((template) => {
                  const spec = getTemplateSpec(template);
                  const metricLabel = METRICS.find((item) => item.key === spec.metric)?.label || spec.metric;
                  const dimensionLabel = DIMENSIONS.find((item) => item.key === spec.dimension)?.label || spec.dimension;
                  return (
                    <TableRow key={template.id} hover sx={{ "&:last-child td": { borderBottom: 0 } }}>
                      <TableCell>
                        <Typography sx={{ fontSize: 13.5, fontWeight: 700, color: C.ink, lineHeight: 1.35 }}>{template.name}</Typography>
                        <Typography sx={{ fontSize: 11.5, color: C.muted, mt: 0.25 }}>Available to this tenant</Typography>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.6 }}>
                          <Chip label={spec.chartType || "Bar"} size="small" sx={{ height: 23, fontSize: 11.5, background: "#F4F0FF", color: C.violet }} />
                          <Chip label={`${metricLabel} by ${dimensionLabel}`} size="small" title={`${metricLabel} by ${dimensionLabel}`} sx={{ height: 23, maxWidth: 220, fontSize: 11.5, background: "#EEF4FF", color: C.blue, "& .MuiChip-label": { overflow: "hidden", textOverflow: "ellipsis" } }} />
                        </Box>
                      </TableCell>
                      <TableCell><Typography sx={{ fontSize: 12.5, color: C.slate }}>{templateFilters(template)}</Typography></TableCell>
                      <TableCell><Typography sx={{ fontSize: 12.5, color: C.slate, whiteSpace: "nowrap" }}>{formatTemplateDate(template)}</Typography></TableCell>
                      <TableCell align="right">
                        <Box sx={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 0.5 }}>
                          <Button size="small" startIcon={<OpenInNewIcon />} onClick={() => openTemplate(template)} sx={{ textTransform: "none", whiteSpace: "nowrap" }}>Open</Button>
                          <Button size="small" startIcon={<ScheduleSendIcon />} onClick={() => openSchedule(template.name, spec)} sx={{ textTransform: "none", color: C.ink, whiteSpace: "nowrap" }}>Schedule</Button>
                          <Tooltip title="More actions"><IconButton size="small" aria-label={`More actions for ${template.name}`} onClick={(event) => { setSelectedTemplate(template); setReportMenuEl(event.currentTarget); }}><MoreVertIcon fontSize="small" /></IconButton></Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>

      <Menu anchorEl={reportMenuEl} open={!!reportMenuEl} onClose={() => { setReportMenuEl(null); setSelectedTemplate(null); }}>
        <MenuItem onClick={() => selectedTemplate && openTemplate(selectedTemplate)}><OpenInNewIcon sx={{ fontSize: 18, mr: 1.25 }} />Open report</MenuItem>
        <MenuItem onClick={() => { if (selectedTemplate) { openSchedule(selectedTemplate.name, getTemplateSpec(selectedTemplate)); setReportMenuEl(null); } }}><ScheduleSendIcon sx={{ fontSize: 18, mr: 1.25 }} />Schedule delivery</MenuItem>
        <MenuItem onClick={() => selectedTemplate && duplicateTemplate(selectedTemplate)} disabled={saving}><ContentCopyIcon sx={{ fontSize: 18, mr: 1.25 }} />Duplicate</MenuItem>
        <MenuItem onClick={() => { setRenameName(selectedTemplate?.name || ""); setRenameOpen(true); setReportMenuEl(null); }}><EditOutlinedIcon sx={{ fontSize: 18, mr: 1.25 }} />Rename</MenuItem>
        <MenuItem onClick={() => { setDeleteTarget(selectedTemplate); setReportMenuEl(null); setSelectedTemplate(null); }} sx={{ color: C.red }}><DeleteOutlineIcon sx={{ fontSize: 18, mr: 1.25 }} />Delete</MenuItem>
      </Menu>

      <Dialog open={templateDialogOpen} onClose={() => setTemplateDialogOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>Save report</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            margin="dense"
            label="Template name"
            value={templateName}
            onChange={(e) => setTemplateName(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") confirmSave(); }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTemplateDialogOpen(false)}>Cancel</Button>
          <Button onClick={confirmSave} disabled={!templateName.trim() || saving} variant="contained">{saving ? <CircularProgress size={18} /> : "Save"}</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={renameOpen} onClose={() => !saving && setRenameOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>Rename saved report</DialogTitle>
        <DialogContent>
          <TextField autoFocus fullWidth margin="dense" label="Report name" value={renameName} onChange={(event) => setRenameName(event.target.value)} onKeyDown={(event) => { if (event.key === "Enter") confirmRename(); }} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRenameOpen(false)} disabled={saving}>Cancel</Button>
          <Button onClick={confirmRename} disabled={!renameName.trim() || saving} variant="contained">{saving ? <CircularProgress size={18} /> : "Rename"}</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)} fullWidth maxWidth="xs">
        <DialogTitle>Delete saved report?</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: 13.5, color: C.slate }}>“{deleteTarget?.name}” will be removed for everyone in this tenant. Existing scheduled deliveries are not affected.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={() => deleteTarget && delTemplate(deleteTarget.id)}>Delete report</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={scheduleOpen} onClose={saving || sendingTest ? undefined : () => { setScheduleOpen(false); setScheduleSpec(null); }} fullWidth maxWidth="sm">
        <DialogTitle>Schedule this report</DialogTitle>
        <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, pt: "12px !important" }}>
          <TextField label="Report name" value={scheduleName} onChange={(e) => setScheduleName(e.target.value)} />
          <FormControl><InputLabel>Cadence</InputLabel><Select label="Cadence" value={scheduleCadence} onChange={(e) => setScheduleCadence(e.target.value)}>
            <MenuItem value="daily">Daily</MenuItem><MenuItem value="weekly">Weekly</MenuItem><MenuItem value="monthly">Monthly</MenuItem>
          </Select></FormControl>
          <TextField label="Email recipients" value={scheduleRecipients} onChange={(e) => setScheduleRecipients(e.target.value)} placeholder="risk@company.com, ops@company.com" helperText="Separate multiple addresses with commas." />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button disabled={saving || sendingTest} onClick={() => { setScheduleOpen(false); setScheduleSpec(null); }}>Cancel</Button>
          <Button variant="outlined" disabled={saving || sendingTest || !tenantId} onClick={sendTest}>{sendingTest ? <CircularProgress size={18} /> : "Send test report"}</Button>
          <Button variant="contained" disabled={saving || sendingTest || !tenantId} onClick={createSchedule}>{saving ? <CircularProgress size={18} /> : "Create schedule"}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ReportBuilder;
